import { TouchableOpacity, View, Pressable, Platform, ScrollView, BackHandler, ActivityIndicator } from "react-native";
import { useStableWindowDimensions } from '@hooks/useStableWindowDimensions';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from '@constants/Themed';
import styles from "@stylesheet";
import LottieView from '@utils/lottieAnimation';
import { HIDE_ALL_MODALS, HIDE_AUTH_MODAL, HIDE_LOADING_MODAL, REFRESH } from '@reduxLocal/actions/types';
import { DefaultRootState } from '@reduxLocal/persistState';
import React, { useEffect, useState, useCallback, useRef } from "react";
import Button from '../../UI/Button/Button';
import * as Animatable from 'react-native-animatable';
import ErrorBoundary from "@utils/ErrorBoundary";
import { Text, XStack, YStack, Progress, Circle, Dialog } from "tamagui";
import { Modal as RNModal } from '@utils/RNModal';
import { useNavigation } from "@react-navigation/native";
import { useResponsiveWeb } from '@hooks/useResponsiveWeb';
import MarkDown from 'react-native-markdown-display';
import { rules } from '@utils/stringUtils/markDownRules';
import { useTranslation } from 'react-i18next';
import NativeProgressBar from '@components/Global/NativeProgressBar';
import {
  AUTH_MODAL_NATIVE_Z_INDEX,
  AUTH_MODAL_WEB_Z_INDEX,
} from '@components/modals/shared/adaptiveModalTheme';

const AuthModal = (props) => {
  const { t } = useTranslation();
  const authModal = useSelector((state: DefaultRootState) => state.modals?.authModal);
  const visibleState = useSelector((state: DefaultRootState) => state.modals?.authModal?.modalDisplayed ?? false);
  const modalRequestId = useSelector((state: DefaultRootState) => state.modals?.authModal?.requestId);
  const theme = useSelector((state: DefaultRootState) => state.app.theme?.lightTheme);
  const windowDimensions = useStableWindowDimensions();
  const { scaling, breakpoints, getResponsiveFontSize } = useResponsiveWeb();
  
  // Modern color palette
  const colors = {
    background: theme ? '#FFFFFF' : '#1A1B23',
    surface: theme ? '#F8FAFC' : '#2A2D3A',
    border: theme ? '#E2E8F0' : '#3F434A',
    text: {
      primary: theme ? '#0F172A' : '#F1F5F9',
      secondary: theme ? '#475569' : '#94A3B8',
      tertiary: theme ? '#64748B' : '#64748B',
    },
    status: {
      success: '#10B981',
      error: '#EF4444',
      warning: '#F59E0B',
      info: '#3B82F6',
      progress: '#06B6D4',
    },
    shadow: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.3)',
    backdrop: 'rgba(0, 0, 0, 0.5)',
  };

  // Modern dynamic styles
  const modernStyles = {
    modalContainer: {
      backgroundColor: theme ? "white" : "black",
      borderRadius: 24 * scaling.scaleFactor,
      padding: 32 * scaling.scaleFactor,
      paddingVertical: 40 * scaling.scaleFactor,
      minWidth: Math.max(320, 380 * scaling.scaleFactor),
      maxWidth: Math.min(windowDimensions.width * 0.9, 500 * scaling.scaleFactor),
      shadowColor: colors.shadow,
      shadowOffset: { width: 0, height: 20 },
      shadowOpacity: 0.25,
      shadowRadius: 25,
      elevation: 20,
      borderWidth: 1,
      borderColor: colors.border,
    },
    iconContainer: {
      minHeight: 120 * scaling.scaleFactor,
      marginBottom: 24 * scaling.scaleFactor,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
    },
    contentContainer: {
      gap: 20 * scaling.scaleFactor,
      alignItems: 'center' as const,
    },
    messageText: {
      fontSize: getResponsiveFontSize(16),
      lineHeight: getResponsiveFontSize(24),
      fontWeight: '600' as const,
      color: colors.text.primary,
      textAlign: 'center' as const,
      letterSpacing: -0.2,
    },
    progressContainer: {
      width: '100%' as const,
      gap: 12 * scaling.scaleFactor,
      alignItems: 'center' as const,
      backgroundColor: colors.surface,
      padding: 20 * scaling.scaleFactor,
      borderRadius: 16 * scaling.scaleFactor,
      borderWidth: 1,
      borderColor: colors.border,
    },
    progressText: {
      fontSize: getResponsiveFontSize(14),
      fontWeight: '500' as const,
      color: colors.text.secondary,
    },
    statusText: {
      fontSize: getResponsiveFontSize(13),
      fontStyle: 'italic' as const,
      color: colors.text.tertiary,
      textAlign: 'center' as const,
    },
    detailsContainer: {
      width: '100%' as const,
      gap: 16 * scaling.scaleFactor,
    },
    detailsToggle: {
      paddingVertical: 12 * scaling.scaleFactor,
      paddingHorizontal: 16 * scaling.scaleFactor,
      borderRadius: 12 * scaling.scaleFactor,
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
    },
    detailsToggleText: {
      fontSize: getResponsiveFontSize(14),
      fontWeight: '500' as const,
      color: colors.status.info,
      textAlign: 'center' as const,
    },
    detailsContent: {
      backgroundColor: colors.surface,
      borderRadius: 16 * scaling.scaleFactor,
      padding: 20 * scaling.scaleFactor,
      borderWidth: 1,
      borderColor: colors.border,
      gap: 16 * scaling.scaleFactor,
    },
    detailsText: {
      fontSize: getResponsiveFontSize(12),
      lineHeight: getResponsiveFontSize(18),
      color: colors.text.secondary,
      fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    },
    buttonContainer: {
      flexDirection: 'row' as const,
      gap: 12 * scaling.scaleFactor,
      width: '100%' as const,
      marginTop: 8 * scaling.scaleFactor,
      justifyContent: "center" as const,
      marginVertical: 10 * scaling.scaleFactor
    },
    button: {
      flex: 1,
      minHeight: 48 * scaling.scaleFactor,
      borderRadius: 12 * scaling.scaleFactor,
      shadowColor: colors.shadow,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
  };
  const [showDetails, setShowDetails] = useState(false);
  const [details, setDetails] = useState<any>();
  const [modalContent, setModalContent] = useState("");
  /** Native RN Modal visibility — kept in sync with Redux, not mount/unmount gated. */
  const [nativeVisible, setNativeVisible] = useState(false);
  const [hasPresentedOnce, setHasPresentedOnce] = useState(false);

  // SSE Progress States
  const [isSSEOperation, setIsSSEOperation] = useState(false);
  const [sseProgress, setSSEProgress] = useState(0);
  const [sseStatus, setSSEStatus] = useState('');
  const [sseMessage, setSSEMessage] = useState('');

  const autoHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Bumps on every SHOW so delayed hide/reset from a prior session cannot wipe a newer one. */
  const sessionRef = useRef(0);
  const isHidingRef = useRef(false);
  const hideModalRef = useRef<() => void>(() => {});
  const lastHandledRequestIdRef = useRef<unknown>(undefined);
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const AUTH_MODAL_OVERLAY_Z_INDEX =
    Platform.OS === 'web' ? AUTH_MODAL_WEB_Z_INDEX : AUTH_MODAL_NATIVE_Z_INDEX;
  const AUTH_MODAL_CONTENT_Z_INDEX = AUTH_MODAL_OVERLAY_Z_INDEX + 1;

  const clearAutoHideTimeout = useCallback(() => {
    if (autoHideTimeoutRef.current) {
      clearTimeout(autoHideTimeoutRef.current);
      autoHideTimeoutRef.current = null;
    }
  }, []);

  const clearResetTimeout = useCallback(() => {
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }
  }, []);

  const resetModalState = useCallback(() => {
    setShowDetails(false);
    setDetails(undefined);
    setModalContent('');
    setIsSSEOperation(false);
    setSSEProgress(0);
    setSSEStatus('');
    setSSEMessage('');
    isHidingRef.current = false;
    clearAutoHideTimeout();
  }, [clearAutoHideTimeout]);

  const hideModal = useCallback(() => {
    if (isHidingRef.current) return;
    isHidingRef.current = true;
    clearAutoHideTimeout();
    setNativeVisible(false);
    dispatch({ type: HIDE_AUTH_MODAL });
  }, [clearAutoHideTimeout, dispatch]);

  hideModalRef.current = hideModal;

  // Sync local UI from Redux; session tokens make hide/reset safe to re-show immediately.
  useEffect(() => {
    if (visibleState && authModal) {
      clearResetTimeout();
      isHidingRef.current = false;

      const isNewSession = modalRequestId !== lastHandledRequestIdRef.current;
      if (isNewSession) {
        sessionRef.current += 1;
        lastHandledRequestIdRef.current = modalRequestId;
        clearAutoHideTimeout();
      }

      const sessionAtSchedule = sessionRef.current;
      setModalContent(authModal.modalContent ?? '');
      setDetails(authModal.details ?? undefined);
      if (authModal.modalType === 'error' && authModal.details) {
        setShowDetails(true);
      } else if (isNewSession) {
        setShowDetails(false);
      }

      if (authModal.modalType === 'progress' || authModal.sseData) {
        setIsSSEOperation(true);
        if (authModal.sseData) {
          const { status, message, progress } = authModal.sseData;
          setSSEStatus(status);
          setSSEMessage(message || '');
          setSSEProgress(progress || 0);

          if (status === 'completed' && !autoHideTimeoutRef.current) {
            autoHideTimeoutRef.current = setTimeout(() => {
              autoHideTimeoutRef.current = null;
              if (sessionRef.current === sessionAtSchedule) {
                hideModalRef.current();
              }
            }, 2000);
          } else if (status === 'error') {
            setIsSSEOperation(false);
          }
        }
      } else {
        setIsSSEOperation(false);
        setSSEProgress(0);
        setSSEStatus('');
        setSSEMessage('');

        if (authModal.modalType === 'success' && !autoHideTimeoutRef.current) {
          autoHideTimeoutRef.current = setTimeout(() => {
            autoHideTimeoutRef.current = null;
            if (sessionRef.current === sessionAtSchedule) {
              hideModalRef.current();
            }
          }, 4000);
        }
      }

      setNativeVisible(true);
      return;
    }

    // Redux says hidden — close native shell, then reset local copy for the *current* session only.
    setNativeVisible(false);
    clearAutoHideTimeout();
    const sessionAtHide = sessionRef.current;
    clearResetTimeout();
    resetTimeoutRef.current = setTimeout(() => {
      resetTimeoutRef.current = null;
      if (sessionRef.current === sessionAtHide) {
        resetModalState();
        lastHandledRequestIdRef.current = undefined;
      }
    }, 300);
  }, [
    authModal,
    authModal?.modalContent,
    authModal?.modalType,
    authModal?.sseData,
    authModal?.sseData?.status,
    authModal?.sseData?.progress,
    authModal?.sseData?.message,
    authModal?.details,
    modalRequestId,
    visibleState,
    clearAutoHideTimeout,
    clearResetTimeout,
    resetModalState,
  ]);

  useEffect(() => {
    return () => {
      clearAutoHideTimeout();
      clearResetTimeout();
    };
  }, [clearAutoHideTimeout, clearResetTimeout]);

  // Helper function to detect errors from content
  const isErrorContent = (content: string | string[] | undefined): boolean => {
    if (!content) return false;
    const contentStr = Array.isArray(content) ? content.join(' ') : content.toString().toLowerCase();
    const errorIndicators = ['403', '401', '404', '500', 'forbidden', 'unauthorized', 'error', 'failed', 'failure'];
    return errorIndicators.some(indicator => contentStr.includes(indicator));
  };

  // Check for error - prioritize error detection
  // Do not sniff AI analysis markdown for words like "error"/"failed"
  const isError = authModal?.modalType === 'error' ||
    (authModal?.modalType !== 'ai-notes' && isErrorContent(modalContent)) ||
    isErrorContent(sseMessage) ||
    (isSSEOperation && sseStatus === 'error');

  /** Keep the determinate bar visible through `completed` so progress does not vanish before the success animation / auto-hide. */
  const sseShowsProgressCard =
    isSSEOperation && sseStatus && sseStatus !== 'error';

  const sseProgressDisplay = Math.min(100, Math.max(0, Math.round(Number(sseProgress) || 0)));

  const renderModalProgress = (progressValue: number | null, options?: { compact?: boolean; fillColor?: string }) => {
    const compact = options?.compact;
    const fillColor = options?.fillColor ?? colors.status.progress;
    const barWidth = compact ? Math.max(50, 60 * scaling.scaleFactor) : '100%';
    const barHeight = compact ? 6 : 8;

    if (Platform.OS !== 'web') {
      if (progressValue == null) {
        return <ActivityIndicator color={fillColor} />;
      }
      return (
        <NativeProgressBar
          value={progressValue}
          max={100}
          height={barHeight}
          backgroundColor={colors.border}
          fillColor={fillColor}
          style={{ width: barWidth }}
        />
      );
    }

    return (
      <Progress
        size={compact ? '$4' : '$6'}
        value={progressValue}
        max={100}
        width={compact ? undefined : '100%'}
        backgroundColor={colors.border}
      >
        <Progress.Indicator
          animation="bouncy"
          backgroundColor={fillColor}
        />
      </Progress>
    );
  };

  const returnTypeIcon = () => {
    let Icon: React.JSX.Element;

    if (isSSEOperation && !isError) {
    // Show different icons based on SSE status (only if not an error)
      switch (sseStatus) {
        case 'starting':
        case 'uploading':
        case 'processing':
          Icon = (
            <View style={{ 
              alignItems: 'center', 
              justifyContent: 'center', 
              width: Math.max(100, 120 * scaling.scaleFactor),
              height: Math.max(100, 120 * scaling.scaleFactor)
            }}>
              <Circle 
                size={Math.max(80, 100 * scaling.scaleFactor)} 
                borderWidth={3} 
                borderColor={colors.status.progress} 
                backgroundColor={colors.surface}
              >
                {renderModalProgress(sseProgress, { compact: true })}
              </Circle>
            </View>
          );
          break;
        case 'progress':
          Icon = (
            <YStack alignItems="center" space="$2">
              <Circle 
                size={Math.max(80, 100 * scaling.scaleFactor)} 
                borderWidth={3} 
                borderColor={colors.status.progress} 
                backgroundColor={colors.surface}
              >
                <Text 
                  fontSize={getResponsiveFontSize(16)} 
                  fontWeight="bold" 
                  color={colors.status.progress}
                >
                  {sseProgress}%
                </Text>
              </Circle>
            </YStack>
          );
          break;
        case 'completed':
          Icon = (
            <LottieView 
              autoPlay 
              style={{ 
                width: Math.max(100, 120 * scaling.scaleFactor), 
                height: Math.max(100, 120 * scaling.scaleFactor) 
              }} 
              source={require('@assets/animations/registerSuccess.json')} 
              loop={false} 
            />
          );
          break;
        case 'error':
          Icon = (
            <LottieView 
              autoPlay 
              style={{ 
                width: Math.max(100, 120 * scaling.scaleFactor), 
                height: Math.max(100, 120 * scaling.scaleFactor) 
              }} 
              source={require('@assets/animations/errorIcon.json')} 
              loop={true} 
              speed={1} 
            />
          );
          break;
        default:
          Icon = (
            <YStack alignItems="center" space="$2">
              <Circle 
                size={Math.max(80, 100 * scaling.scaleFactor)} 
                borderWidth={3} 
                borderColor={colors.status.progress} 
                backgroundColor={colors.surface} 
                justifyContent="center" 
                alignItems="center"
              >
                {renderModalProgress(null, { compact: true })}
              </Circle>
              <Text 
                fontSize={getResponsiveFontSize(12)} 
                color={colors.text.secondary}
              >
                {t('Loading...')}
              </Text>
            </YStack>
          );
      }
    } else if (isError || authModal?.modalType === 'error') {
      // Show error icon for any error condition
      Icon = (
        <LottieView 
          autoPlay 
          style={{ 
            width: Math.max(100, 120 * scaling.scaleFactor), 
            height: Math.max(100, 120 * scaling.scaleFactor) 
          }} 
          source={require('@assets/animations/errorIcon.json')} 
          loop={true} 
          speed={1} 
        />
      );
    } else if (authModal?.modalType === 'warning') {
      Icon = (
        <LottieView 
          autoPlay 
          style={{ 
            width: Math.max(100, 120 * scaling.scaleFactor), 
            height: Math.max(100, 120 * scaling.scaleFactor) 
          }} 
          source={require('@assets/animations/88006-warning-yellow.json')} 
          loop={true} 
          speed={1} 
        />
      );
    } else if (authModal?.modalType === 'loading') {
      Icon = (
        <LottieView
          autoPlay
          style={{
            width: Math.max(100, 120 * scaling.scaleFactor),
            height: Math.max(100, 120 * scaling.scaleFactor)
          }}
          source={require('@assets/animations/spin-leaf-loader.json')}
          loop={true}
          speed={1}
        />
      );
    } else if (authModal?.modalType === 'ai-notes') {
      Icon = (
        <LottieView
          autoPlay
          style={{
            width: Math.max(100, 120 * scaling.scaleFactor),
            height: Math.max(100, 120 * scaling.scaleFactor)
          }}
          source={require('@assets/animations/automation_robot.json')}
          loop={true}
        />
      );
    } else {
      Icon = (
        <LottieView 
          autoPlay 
          style={{ 
            width: Math.max(100, 120 * scaling.scaleFactor), 
            height: Math.max(100, 120 * scaling.scaleFactor) 
          }} 
          source={require('@assets/animations/registerSuccess.json')} 
          loop={true} 
        />
      );
    }
    return Icon;
  };

  // Handle navigation logic with proper checks
  useEffect(() => {
    if (authModal?.modalContent?.includes?.("User not found") && visibleState) {
      // Add small delay to prevent immediate navigation and allow user to see the message
      const navigationTimeout = setTimeout(() => {
        hideModal();
        (navigation as any).navigate("Login");
      }, 2000);

      return () => clearTimeout(navigationTimeout);
    }
  }, [authModal?.modalContent, visibleState, navigation, hideModal]);
  // Prefer local copy, fall back to Redux so a re-show never renders an empty shell
  // while the sync effect is still flushing.
  const resolvedModalContent = modalContent || authModal?.modalContent || '';
  const messages = Array.isArray(resolvedModalContent)
    ? resolvedModalContent.join('\n')
    : resolvedModalContent?.toString() || '';

  // Determine the display message - prioritize SSE message for active operations
  const resolvedSseMessage = sseMessage || authModal?.sseData?.message || '';
  const displayMessage = isSSEOperation && resolvedSseMessage ? resolvedSseMessage : messages;
  const isSchedulingMessage = /task scheduled|schedule/i.test(displayMessage || '');
  const showViewTasksAction = !isSSEOperation && authModal?.modalType === 'success' && isSchedulingMessage;

  // Handle Android hardware back button
  useEffect(() => {
    if (Platform.OS !== 'android' || !visibleState || isHidingRef.current) return;

    const backHandler = BackHandler.addEventListener('hardwareBackPress', () => {
      if ((!isSSEOperation || sseStatus === 'error' || sseStatus === 'completed') && authModal?.modalType !== 'loading') {
        hideModal();
      }
      return true; // Prevent default behavior
    });

    return () => backHandler.remove();
  }, [visibleState, isSSEOperation, sseStatus, authModal?.modalType, hideModal]);

  const hasDisplayableContent = !!(
    modalContent ||
    isSSEOperation ||
    authModal?.modalContent ||
    authModal?.sseData
  );
  const shouldPresent =
    (Platform.OS === 'web' ? !!visibleState : !!(nativeVisible || visibleState)) &&
    !!authModal &&
    hasDisplayableContent;

  useEffect(() => {
    if (shouldPresent) {
      setHasPresentedOnce(true);
    }
  }, [shouldPresent]);

  // Web unmounts when closed. Native mounts on first present, then toggles `visible`.
  if (Platform.OS === 'web' && !shouldPresent) {
    return null;
  }

  if (Platform.OS !== 'web' && !hasPresentedOnce && !shouldPresent) {
    return null;
  }

  const innerContent = (
    <>
      {/* Header */}
      <View style={{
        backgroundColor: theme ? '#f8fafc' : '#374151',
        paddingHorizontal: 24,
        paddingVertical: 20,
        borderRadius: 16,
        borderBottomWidth: 1,
        borderBottomColor: theme ? '#e5e7eb' : '#4b5563',
      }}>
        <XStack alignItems="center" gap="$3">
          <View style={{
            backgroundColor: isError || authModal?.modalType === 'error'
              ? (theme ? "#fee2e2" : "#7f1d1d")
              : sseStatus === 'completed'
                ? (theme ? "#d1fae5" : "#064e3b")
                : (theme ? "#f0f9ff" : "#0c4a6e"),
            borderRadius: 16,
            padding: 10,
            height: 100,
            width: 100,
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {returnTypeIcon()}
          </View>
          <YStack flex={1}>
            <Dialog.Title style={{
              fontSize: 20,
              fontWeight: '700',
              color: theme ? '#1e293b' : '#f1f5f9',
              marginBottom: 4,
              margin: 0,
            }}>
              {isSSEOperation
                ? sseStatus === 'completed'
                  ? t('Operation Complete')
                  : sseStatus === 'error'
                    ? t('Operation Failed')
                    : t('Operation in Progress')
                : authModal?.modalType === 'ai-notes'
                  ? t('AI Analysis')
                  : authModal?.modalType === 'success' && isSchedulingMessage
                  ? t('Task Scheduled')
                  : authModal?.modalType === 'error' && isSchedulingMessage
                    ? t('Scheduling Failed')
                    : authModal?.modalType === 'error'
                      ? t('Error')
                      : authModal?.modalType === 'warning'
                        ? t('Warning')
                        : authModal?.modalType === 'loading'
                          ? t('Please wait...')
                          : t('Notification')}
            </Dialog.Title>
            <Dialog.Description style={{
              fontSize: 14,
              color: theme ? '#64748b' : '#94a3b8',
              fontWeight: '500',
              margin: 0,
            }}>
              {authModal?.modalType === 'ai-notes'
                ? t('Review the AI insights for this dataset')
                : isSSEOperation && sseStatus
                ? `Status: ${sseStatus.charAt(0).toUpperCase() + sseStatus.slice(1)}`
                : t('Please review the details below')}
            </Dialog.Description>
          </YStack>
        </XStack>
      </View>

      {/* Content */}
      <View>
        <ErrorBoundary>
          <YStack gap="$4">
            {/* Main Message */}
            <View style={{
              backgroundColor: theme ? '#f8fafc' : '#374151',
              borderRadius: 16,
              padding: 20,
              borderWidth: 1,
              borderColor: theme ? '#e5e7eb' : '#4b5563',
            }}>
              {authModal?.modalType === 'ai-notes' ? (
                <ScrollView
                  style={{
                    maxHeight: Math.min(windowDimensions.height * 0.5, 500),
                    minHeight: 120,
                  }}
                  nestedScrollEnabled
                  showsVerticalScrollIndicator={true}
                  contentContainerStyle={{ paddingBottom: 8 }}
                >
                  <MarkDown
                    rules={rules}
                    style={{
                      body: {
                        fontSize: 16,
                        lineHeight: 24,
                        fontWeight: '500',
                        color: theme ? '#1e293b' : '#f1f5f9',
                      },
                      paragraph: {
                        fontSize: 16,
                        lineHeight: 24,
                        fontWeight: '500',
                        color: theme ? '#1e293b' : '#f1f5f9',
                        marginVertical: 4,
                      },
                      heading1: {
                        fontSize: 20,
                        fontWeight: 'bold',
                        color: theme ? '#1e293b' : '#f1f5f9',
                        marginVertical: 8,
                      },
                      heading2: {
                        fontSize: 18,
                        fontWeight: 'bold',
                        color: theme ? '#1e293b' : '#f1f5f9',
                        marginVertical: 6,
                      },
                      heading3: {
                        fontSize: 16,
                        fontWeight: '600',
                        color: theme ? '#1e293b' : '#f1f5f9',
                        marginVertical: 4,
                      },
                      code_inline: {
                        backgroundColor: theme ? '#f1f5f9' : '#374151',
                        paddingHorizontal: 4,
                        paddingVertical: 2,
                        borderRadius: 3,
                        fontSize: 14,
                        fontFamily: 'monospace',
                        color: theme ? '#1e293b' : '#f1f5f9',
                      },
                      code_block: {
                        backgroundColor: theme ? '#f8fafc' : '#374151',
                        padding: 8,
                        borderRadius: 4,
                        marginVertical: 4,
                        fontSize: 14,
                        fontFamily: 'monospace',
                        color: theme ? '#1e293b' : '#f1f5f9',
                      },
                      blockquote: {
                        borderLeftWidth: 3,
                        borderLeftColor: theme ? '#cbd5e1' : '#64748b',
                        paddingLeft: 8,
                        marginVertical: 4,
                        backgroundColor: theme ? '#f8fafc' : '#374151',
                      },
                      list_item: {
                        fontSize: 16,
                        lineHeight: 24,
                        color: theme ? '#1e293b' : '#f1f5f9',
                        marginVertical: 2,
                      },
                      strong: {
                        fontWeight: 'bold',
                        color: theme ? '#1e293b' : '#f1f5f9',
                      },
                      em: {
                        fontStyle: 'italic',
                        color: theme ? '#1e293b' : '#f1f5f9',
                      },
                    }}
                  >
                    {(typeof displayMessage === 'string' && displayMessage.trim().length > 0)
                      ? displayMessage
                      : t('AI was unable to analyse the data')}
                  </MarkDown>
                </ScrollView>
              ) : (
                <Text style={{
                  fontSize: 16,
                  lineHeight: 24,
                  fontWeight: '500',
                  color: theme ? '#1e293b' : '#f1f5f9',
                  textAlign: 'left',
                }}>
                  {displayMessage}
                </Text>
              )}
            </View>

            {/* SSE upload pipeline (starting → uploading → processing → completed) */}
            {sseShowsProgressCard && (
              <View style={modernStyles.progressContainer}>
                {sseMessage ? (
                  <Text style={[modernStyles.statusText, { maxWidth: '100%' }]} numberOfLines={3}>
                    {sseMessage}
                  </Text>
                ) : null}
                {renderModalProgress(
                  sseStatus === 'completed' ? 100 : sseProgressDisplay,
                  {
                    fillColor: sseStatus === 'completed' ? colors.status.success : colors.status.progress,
                  }
                )}
                <Text style={modernStyles.progressText}>
                  {t('uploadProgressPercent', { percent: sseStatus === 'completed' ? 100 : sseProgressDisplay })}
                </Text>
              </View>
            )}

            {/* Details Section */}
            {details && details != undefined && (
              <View style={{
                backgroundColor: theme ? '#f8fafc' : '#374151',
                borderRadius: 16,
                borderWidth: 1,
                borderColor: theme ? '#e5e7eb' : '#4b5563',
                overflow: 'hidden',
              }}>
                <Pressable
                  onPress={() => setShowDetails(!showDetails)}
                  style={{
                    paddingVertical: 12,
                    paddingHorizontal: 16,
                    backgroundColor: theme ? '#ffffff' : '#1f2937',
                    borderBottomWidth: showDetails ? 1 : 0,
                    borderBottomColor: theme ? '#e5e7eb' : '#4b5563',
                  }}
                >
                  <Text style={{
                    fontSize: 14,
                    fontWeight: '600',
                    color: colors.status.info,
                    textAlign: 'center',
                  }}>
                    {showDetails ? 'Hide details' : 'Show details'}
                  </Text>
                </Pressable>

                {showDetails && (
                  <View style={{
                    padding: 20,
                    gap: 16,
                  }}>
                    <ScrollView
                      style={{ maxHeight: 200 }}
                      nestedScrollEnabled
                    >
                      {details && Array.isArray(details.logs) && details.logs.length > 0 ? (
                        <YStack gap="$2">
                          {(details.failedStage != null || details.stageIndex != null) && (
                            <Text style={{
                              fontSize: 12,
                              fontWeight: '600',
                              color: theme ? '#64748b' : '#94a3b8',
                              fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
                            }}>
                              Failed at stage: {details.failedStage ?? (details.stageIndex + 1)}
                            </Text>
                          )}
                          {details.logs.map((entry: any, i: number) => (
                            <Text key={i} style={{
                              fontSize: 11,
                              lineHeight: 16,
                              color: theme ? '#64748b' : '#94a3b8',
                              fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
                            }}>
                              {typeof entry === 'string' ? entry : (entry?.message ?? entry?.at ? `[${entry.at}] ${entry.message ?? ''}` : JSON.stringify(entry))}
                            </Text>
                          ))}
                        </YStack>
                      ) : (
                        <Text style={{
                          fontSize: 12,
                          lineHeight: 18,
                          color: theme ? '#64748b' : '#94a3b8',
                          fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
                        }}>
                          {JSON.stringify(details ?? 'No details available for this error', null, 2)}
                        </Text>
                      )}
                    </ScrollView>
                    <Pressable
                      onPress={() => console.log('handle flag error!')}
                      style={{
                        backgroundColor: colors.status.warning,
                        borderRadius: 12,
                        paddingVertical: 12,
                        paddingHorizontal: 20,
                        alignItems: 'center',
                        marginTop: 8,
                      }}
                    >
                      <Text style={{
                        color: '#ffffff',
                        fontWeight: '600',
                        fontSize: 14,
                      }}>
                        {t('Flag Issue')}
                      </Text>
                    </Pressable>
                  </View>
                )}
              </View>
            )}
          </YStack>
        </ErrorBoundary>
      </View>

      {/* Footer Actions */}
      <View style={{
        backgroundColor: theme ? '#f8fafc' : '#374151',
        paddingHorizontal: 24,
        paddingVertical: 16,
        borderRadius: 16,
        borderTopWidth: 1,
        borderTopColor: theme ? '#e5e7eb' : '#4b5563',
      }}>
        <XStack gap="$3" flexWrap="wrap">
          {/* Cancel button for active SSE operations */}
          {isSSEOperation && sseStatus !== 'completed' && sseStatus !== 'error' && (
            <Pressable
              onPress={() => {
                if (!isHidingRef.current) {
                  hideModal();
                }
              }}
              style={{
                paddingHorizontal: 20,
                paddingVertical: 12,
                borderRadius: 12,
                backgroundColor: theme ? '#f1f5f9' : '#4b5563',
                borderWidth: 1,
                borderColor: theme ? '#e2e8f0' : '#6b7280',
                minWidth: 100,
                flex: 1,
                alignItems: 'center',
              }}
            >
              <Text style={{
                color: theme ? '#64748b' : '#d1d5db',
                fontWeight: '600',
                fontSize: 14,
              }}>
                {t('Cancel')}
              </Text>
            </Pressable>
          )}

          {/* Upgrade button for usage limits */}
          {!isSSEOperation && messages.includes("Usage limits exceeded") && (
            <Pressable
              onPress={() => {
                if (!isHidingRef.current) {
                  hideModal();
                  (navigation as any).navigate("Settings", {
                    screen: "Upgrade"
                  });
                  setTimeout(() => dispatch({ type: "UPGRADE_TAB" }), 200);
                }
              }}
              style={{
                paddingHorizontal: 20,
                paddingVertical: 12,
                borderRadius: 12,
                flex: 1,
                backgroundColor: colors.status.info,
                minWidth: 100,
                alignItems: 'center',
                elevation: 2,
                shadowColor: colors.status.info,
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.2,
                shadowRadius: 8,
              }}
            >
              <Text style={{
                color: '#ffffff',
                fontWeight: '600',
                fontSize: 14,
              }}>
                {t('Upgrade')}
              </Text>
            </Pressable>
          )}

          {/* View tasks button for background-scheduled AI work */}
          {showViewTasksAction && (
            <Pressable
              onPress={() => {
                if (!isHidingRef.current) {
                  hideModal();
                  setTimeout(() => {
                    (navigation as any).navigate('Users', {
                      screen: 'AgentTasks'
                    });
                  }, 150);
                }
              }}
              style={{
                paddingHorizontal: 20,
                paddingVertical: 12,
                borderRadius: 12,
                backgroundColor: colors.status.info,
                minWidth: 120,
                alignItems: 'center',
                elevation: 2,
                shadowColor: colors.status.info,
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.2,
                shadowRadius: 8,
              }}
            >
              <Text style={{
                color: '#ffffff',
                fontWeight: '600',
                fontSize: 14,
              }}>
                {t('View Tasks')}
              </Text>
            </Pressable>
          )}

          {/* Close/Done button */}
          {(!isSSEOperation || sseStatus === 'error' || sseStatus === 'completed') && authModal?.modalType !== 'loading' && (
            <Pressable
              onPress={() => {
                if (!isHidingRef.current) {
                  hideModal();
                }
              }}
              style={{
                paddingHorizontal: 20,
                paddingVertical: 12,
                borderRadius: 12,
                backgroundColor: sseStatus === 'completed'
                  ? colors.status.success
                  : (isError || authModal?.modalType === 'error')
                    ? colors.status.error
                    : theme ? '#3b82f6' : '#2563eb',
                minWidth: 100,
                alignItems: 'center',
                elevation: 2,
                shadowColor: sseStatus === 'completed'
                  ? colors.status.success
                  : colors.status.error,
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.2,
                shadowRadius: 8,
              }}
            >
              <Text style={{
                color: '#ffffff',
                fontWeight: '600',
                fontSize: 14,
              }}>
                {sseStatus === 'completed' ? "Done" : "Close"}
              </Text>
            </Pressable>
          )}
        </XStack>
      </View>
    </>
  );

  const containerStyle = {
    zIndex: AUTH_MODAL_CONTENT_Z_INDEX,
    padding: 0,
    gap: 20,
    maxHeight: "85%" as const,
    maxWidth: "90%" as const,
    minWidth: Math.max(300, 380 * scaling.scaleFactor),
    borderRadius: 24,
    overflow: 'hidden' as const,
    justifyContent: 'center' as const,
    alignItems: 'stretch' as const,
    alignSelf: 'center' as const,
    elevation: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.15,
    shadowRadius: 32,
    backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
    borderWidth: 1,
    borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
  };

  const overlayStyle = {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: AUTH_MODAL_OVERLAY_Z_INDEX,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
  };

  if (Platform.OS === 'web') {
    return (
      <Dialog open={visibleState} onOpenChange={(open) => {
        if (!open) {
          if ((!isSSEOperation || sseStatus === 'error' || sseStatus === 'completed') && authModal?.modalType !== 'loading') {
            hideModal();
          }
        }
      }}>
        <Dialog.Portal zIndex={AUTH_MODAL_OVERLAY_Z_INDEX}>
          <Dialog.Overlay
            key="auth-modal-overlay"
            animation="quick"
            opacity={0.6}
            style={{
              ...overlayStyle,
              backdropFilter: 'blur(8px)',
            }}
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
            onPress={() => {
              if ((!isSSEOperation || sseStatus === 'error' || sseStatus === 'completed') && authModal?.modalType !== 'loading') {
                hideModal();
              }
            }}
          />
          <Dialog.Content
            key="auth-modal-content"
            bordered
            elevate
            animation={[
              'quick',
              { opacity: { overshootClamping: true } },
            ]}
            enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
            exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
            x={0}
            scale={1}
            style={[
              containerStyle,
              {
                backdropFilter: 'blur(24px)',
                boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
                maxWidth: 1280
              }
            ]}
            opacity={1}
            y={0}
          >
            {innerContent}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>
    );
  }

  // Native mail login uses Tamagui Sheet `modal`, which presents a system RN Modal.
  // A Tamagui Portal cannot stack above that window regardless of zIndex, so AuthModal
  // must present as its own RN Modal (overFullScreen) to appear on top.
  // Keep the Modal mounted and toggle `visible` so dismiss → re-show is reliable.
  return (
    <RNModal
      visible={!!shouldPresent}
      transparent
      animationType="fade"
      presentationStyle="overFullScreen"
      statusBarTranslucent
      onRequestClose={() => {
        if ((!isSSEOperation || sseStatus === 'error' || sseStatus === 'completed') && authModal?.modalType !== 'loading') {
          hideModal();
        }
      }}
    >
      <View
        style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {shouldPresent ? (
          <>
            <Pressable
              style={overlayStyle}
              onPress={() => {
                if ((!isSSEOperation || sseStatus === 'error' || sseStatus === 'completed') && authModal?.modalType !== 'loading') {
                  hideModal();
                }
              }}
            />
            <Animatable.View
              key={`auth-modal-${String(modalRequestId ?? 'open')}`}
              animation="zoomIn"
              duration={200}
              style={containerStyle}
            >
              {innerContent}
            </Animatable.View>
          </>
        ) : null}
      </View>
    </RNModal>
  );
};

export default AuthModal